import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2.5rem;
  justify-content: center;
  
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    padding: 0 1rem;
  }
  
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const FilterButton = styled(motion.button)`
  background: ${props => props.active ? props.activeColor || 'var(--primary)' : 'transparent'};
  color: ${props => props.active ? 'var(--white)' : 'rgba(255, 255, 255, 0.8)'};
  border: 2px solid ${props => props.active ? props.activeColor || 'var(--primary)' : 'rgba(255, 255, 255, 0.3)'};
  padding: 0.6rem 1.5rem;
  border-radius: var(--border-radius-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast);
  position: relative;
  overflow: hidden;
  
  .dark-mode & {
    color: ${props => props.active ? 'var(--white)' : 'var(--light)'};
    border-color: ${props => props.active ? props.activeColor || 'var(--primary)' : 'var(--gray-700)'};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.hoverColor || 'var(--primary)'};
    transform: translateX(-100%);
    transition: transform var(--transition-fast);
    z-index: -1;
  }
  
  &:hover {
    color: var(--white);
    border-color: ${props => props.hoverColor || 'var(--primary)'};
    
    &::before {
      transform: translateX(0);
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.3);
  }
`;

const FilterIndicator = styled(motion.span)`
  display: block;
  position: absolute;
  bottom: -2px;
  left: 0;
  height: 2px;
  background-color: ${props => props.color || 'var(--primary)'};
`;

const ProjectFilter = ({ categories, activeCategory, setActiveCategory, categoryColors }) => {
  // Définir les couleurs pour chaque catégorie
  const getCategoryColor = (category) => {
    switch(category) {
      case 'all':
        return 'var(--primary)';
      case 'site-vitrine':
        return categoryColors?.siteVitrine || 'var(--black)';
      case 'e-commerce':
        return categoryColors?.ecommerce || 'var(--secondary)';
      case 'application':
        return categoryColors?.application || 'var(--accent)';
      default:
        return 'var(--primary)';
    }
  };
  
  const getCategoryName = (category) => {
    switch(category) {
      case 'all':
        return 'Tous les projets';
      case 'site-vitrine':
        return 'Sites Vitrine';
      case 'e-commerce':
        return 'E-commerce';
      case 'application':
        return 'Applications Web';
      default:
        return category;
    }
  };
  
  const buttonVariants = {
    initial: { y: 20, opacity: 0 },
    animate: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.4,
        ease: "easeOut"
      }
    }),
    tap: { scale: 0.95 }
  };
  
  return (
    <FilterContainer>
      {categories.map((category, index) => (
        <FilterButton
          key={category}
          active={activeCategory === category}
          activeColor={getCategoryColor(category)}
          hoverColor={getCategoryColor(category)}
          onClick={() => setActiveCategory(category)}
          variants={buttonVariants}
          custom={index}
          initial="initial"
          animate="animate"
          whileTap="tap"
          whileHover={{ y: -5 }}
        >
          {getCategoryName(category)}
          {activeCategory === category && (
            <FilterIndicator
              layoutId="filterIndicator"
              color={getCategoryColor(category)}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          )}
        </FilterButton>
      ))}
    </FilterContainer>
  );
};

export default ProjectFilter;
